import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  LayoutDashboard,
  FileText,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Bell,
  BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const Logo = () => (
  <Link to="/" className="flex items-center gap-2.5">
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="1" width="14" height="6" rx="1" fill="#f43f5e" />
      <rect x="16" y="1" width="16" height="6" rx="1" fill="#e11d48" />
      <rect x="0" y="9" width="9" height="6" rx="1" fill="#e11d48" />
      <rect x="11" y="9" width="11" height="6" rx="1" fill="#f43f5e" />
      <rect x="24" y="9" width="8" height="6" rx="1" fill="#e11d48" />
      <rect x="0" y="17" width="15" height="6" rx="1" fill="#f43f5e" />
      <rect x="17" y="17" width="15" height="6" rx="1" fill="#e11d48" />
      <rect x="0" y="25" width="10" height="6" rx="1" fill="#e11d48" />
      <rect x="12" y="25" width="12" height="6" rx="1" fill="#f43f5e" />
      <rect x="26" y="25" width="6" height="6" rx="1" fill="#e11d48" />
    </svg>
    <span className="text-lg font-semibold tracking-tight text-white">
      saleswall
    </span>
  </Link>
);

const adminNavItems = [
  { label: 'Dashboard', href: '/admin', icon: LayoutDashboard },
  { label: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
  { label: 'All Pitches', href: '/admin/pitches', icon: FileText },
];

interface AdminLayoutProps {
  children: React.ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href: string) => {
    if (href === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={`fixed top-0 left-0 z-50 h-screen w-64 bg-[#0f0f10] border-r border-white/5 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="h-16 flex items-center justify-between px-4 border-b border-white/5">
            <Logo />
            <button
              className="lg:hidden p-1 text-zinc-400 hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-4 py-3 border-b border-white/5">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-500/10 text-rose-400 text-xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
              Admin Console
            </span>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {adminNavItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.href)
                    ? 'bg-rose-500 text-white'
                    : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-rose-500 flex items-center justify-center">
                <span className="text-sm font-semibold text-white">
                  {user?.name?.charAt(0) || 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-zinc-500 truncate">Administrator</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full justify-start text-zinc-400 hover:text-white hover:bg-white/5"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 h-16 bg-[#0a0a0b]/80 backdrop-blur-xl border-b border-white/5 px-4 lg:px-6">
          <div className="flex items-center justify-between h-full">
            <button
              className="lg:hidden p-2 text-zinc-400 hover:text-white"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="hidden lg:flex items-center gap-2 text-sm">
              <span className="text-zinc-500">Admin Console</span>
              <ChevronRight className="w-4 h-4 text-zinc-600" />
              <span className="text-white font-medium">
                {adminNavItems.find(item => isActive(item.href))?.label || 'Dashboard'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button className="p-2 text-zinc-500 hover:text-white hover:bg-white/5 rounded-lg transition-colors relative">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full"></span>
              </button>
            </div>
          </div>
        </header>

        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
